/* My School — handoff 03-Screens/06-My-School.png. Data: brightivity.dev/school (shared with StudioCore's org queue).
 * Org admins: the handoff queue (stat cards, status chips, Approve all, rows) doing what the StudioCore console does
 * (whole documents: approve → every page on Print, reject with a reason), then Users / Licenses / Activity from the
 * forked StudioCore console (credit pool, license keys, roster — components/orgAdmin). Everyone else: their requests. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../app/AppShell.jsx';
import { approveGroup, approveJob, cancelJob, dismissJob, jobImage, listMine, listQueue, rejectGroup, rejectJob, schoolMe } from '../api/school.js';
import UsersTab from '../components/orgAdmin/UsersTab.jsx';
import LicensesTab from '../components/orgAdmin/LicensesTab.jsx';
import ActivityTab from '../components/orgAdmin/ActivityTab.jsx';
import { useOrgQueue } from '../contexts/OrgQueueContext.jsx';
import { handToPrint } from '../features/print/handoff.js';
import { Button, Card, Chip, EmptyState, IconTile, Modal, Skeleton, Spinner, StatCard, Tabs, Tag } from '../ui/index.jsx';
import '../features/school/school.css';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/index.js';

const STATUS = {
  pending: { label: 'school.status.pending', color: 'yellow', dot: 'yellow' },
  approved: { label: 'status.approved', color: 'green', dot: 'green' },
  printed: { label: 'status.printed', color: 'blue', dot: 'blue' },
  failed: { label: 'school.status.failed', color: 'coral', dot: 'coral' },
  rejected: { label: 'status.rejected', color: 'purple', dot: 'grey' },
  cancelled: { label: 'status.cancelled', color: 'purple', dot: 'grey' },
  expired: { label: 'status.expired', color: 'purple', dot: 'grey' },
};
const view = (j) => (j.status === 'approved' && j.print_failed ? 'failed' : j.status);
const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  const t = d.toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `${i18n.t('bv:time.today')}, ${t}`;
  if (d.toDateString() === y.toDateString()) return `${i18n.t('bv:common.yesterday')}, ${t}`;
  return `${d.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}, ${t}`;
};
function MyRequests({ me }) {
  const { t } = useTranslation('bv');
  const [jobs, setJobs] = useState(null);
  const load = useCallback(() => listMine().then((r) => setJobs(r.ok ? r.jobs || [] : [])), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!jobs) return <Skeleton height={200} radius={24} />;
  return (
    <div className="bv-school__stack" data-testid="my-requests">
      <Card className="bv-school__intro">
        <IconTile icon="school" color="yellow" />
        <div>
          <b>{me.schoolName || t('school.yourSchool')}</b>
          <p className="bv-small bv-muted">{me.canPrint ? t('school.mine.direct') : me.schoolHasApprover ? t('school.mine.approver') : t('school.mine.noApprover')}</p>
        </div>
      </Card>
      {jobs.length === 0 ? <Card><EmptyState icon="print" title={t('school.mine.emptyTitle')}>{t('school.mine.emptyBody')}</EmptyState></Card> : (
        <div className="bv-school__rows">
          {jobs.map((j) => {
            const s = STATUS[view(j)] || STATUS.pending;
            return (
              <Card key={j.id} className="bv-qrow" data-testid="my-request">
                <div className="bv-qrow__thumb">{j.thumbUrl ? <img src={j.thumbUrl} alt="" /> : <IconTile icon="image" color="white" />}</div>
                <div className="bv-qrow__main">
                  <div className="bv-qrow__title"><span className="bv-qrow__name">{j.design_name || t('common.poster')}</span><Tag color={s.color}>{t(s.label)}</Tag></div>
                  <div className="bv-qrow__meta">{when(j.created_at)}{j.decision_note ? ` · “${j.decision_note}”` : ''}</div>
                </div>
                <div className="bv-qrow__actions">
                  {j.status === 'pending' ? <Button onClick={async () => { await cancelJob(j.id); load(); }}>{t('school.mine.cancelRequest')}</Button>
                    : <Button onClick={async () => { await dismissJob(j.id); load(); }}>{t('common.remove')}</Button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- org admin: handoff 06-My-School.png */
const STATUS_ROW = {
  pending: { label: 'school.status.pending', color: 'yellow', dot: 'yellow' },
  approved: { label: 'status.approved', color: 'green', dot: 'green' },
  printed: { label: 'status.printed', color: 'blue', dot: 'blue' },
  failed: { label: 'school.status.failed', color: 'coral', dot: 'coral' },
  rejected: { label: 'status.rejected', color: 'purple', dot: 'grey' },
};
const money = (n) => (n == null ? null : `$${Number(n).toFixed(2)}`);
const thisMonth = (iso) => { if (!iso) return false; const d = new Date(iso); const n = new Date(); return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth(); };
const sizeOf = (s) => {
  if (!s) return null;
  const w = s.customWidthIn || s.widthIn;
  const h = s.customHeightIn || s.heightIn || s.printHeightIn;
  if (w && h) return `${Math.round(w * 10) / 10} × ${Math.round(h * 10) / 10} ${i18n.t('bv:school.inch')}`;
  return w ? i18n.t('bv:school.inWide', { w: Math.round(w * 10) / 10 }) : null;
};
/** Multi-page documents share a group_id (desktop PrintQueueTab groups them: one row, "· N pages"). */
function groupJobs(list) {
  const by = new Map();
  for (const j of list) {
    const k = j.group_id || j.id;
    if (!by.has(k)) by.set(k, { id: k, groupId: j.group_id || null, jobs: [] });
    by.get(k).jobs.push(j);
  }
  return [...by.values()];
}
const pagesToPrint = (items) => items.filter((p) => p.imageUrl).map((p) => ({ remote: true, name: p.designName || p.design_name || i18n.t('bv:common.poster'), previewUrl: p.thumbUrl || p.imageUrl, sourceUrl: p.imageUrl, schoolJobId: p.jobId || p.id }));

function QueueRow({ g, busy, onApprove, onReject, onPrint, onPreview }) {
  const { t } = useTranslation('bv');
  const j = g.jobs[0];
  const v = view(j);
  const s = STATUS_ROW[v] || STATUS_ROW.pending;
  const ink = g.jobs.reduce((a, x) => a + Number(x.ink_estimate_cost_usd || 0), 0);
  const meta = [j.requester_name || j.requester_email, when(j.created_at), sizeOf(j.print_settings), ink ? t('school.aboutInk', { money: money(ink) }) : null].filter(Boolean);
  return (
    <Card className="bv-qrow" data-testid="queue-row">
      <div className="bv-qrow__thumb">{j.thumbUrl ? <img src={j.thumbUrl} alt="" loading="lazy" /> : <IconTile icon="image" color="white" />}</div>
      <div className="bv-qrow__main">
        <div className="bv-qrow__title">
          <span className="bv-qrow__name">{j.design_name || t('common.poster')}</span>
          {g.jobs.length > 1 && <span className="bv-qrow__pages">{t('canva.pages', { count: g.jobs.length })}</span>}
          <Tag color={s.color}>{t(s.label)}</Tag>
        </div>
        <div className="bv-qrow__meta"><b>{meta[0]}</b>{meta.slice(1).map((m, i) => <span key={i}> · {m}</span>)}</div>
        {j.decision_note && <div className="bv-small bv-muted">“{j.decision_note}”</div>}
      </div>
      <div className="bv-qrow__actions">
        {v === 'pending' && <>
          <Button disabled={busy} onClick={onReject}>{t('school.reject')}</Button>
          <Button variant="green" icon="check" disabled={busy} onClick={onApprove} title={t('school.approveTitle')}>{busy ? t('school.approving') : t('school.approve')}</Button>
        </>}
        {v === 'approved' && <>
          <Button icon="eye" onClick={onPreview}>{t('common.preview')}</Button>
          <Button variant="blue" icon="print" disabled={busy} onClick={onPrint}>{t('print.print')}</Button>
        </>}
        {v === 'failed' && <>
          <Button icon="eye" onClick={onPreview}>{t('common.preview')}</Button>
          <Button variant="coral" icon="redo" disabled={busy} onClick={onPrint}>{t('common.tryAgain')}</Button>
        </>}
        {(v === 'printed' || v === 'rejected') && !j.image_purged_at && <Button icon="eye" onClick={onPreview}>{t('common.preview')}</Button>}
        {(v === 'printed' || v === 'rejected') && j.image_purged_at && <span className="bv-small bv-muted">{t('school.purged')}</span>}
      </div>
    </Card>
  );
}

function PreviewModal({ job, onClose }) {
  const { t } = useTranslation('bv');
  const [img, setImg] = useState(null);
  useEffect(() => { jobImage(job.id).then((r) => setImg(r.ok ? r.thumbUrl || r.imageUrl : false)); }, [job.id]);
  return (
    <Modal open title={job.design_name || t('common.poster')} onClose={onClose} width={720}>
      <div className="bv-qpreview">{img === null ? <Spinner /> : img ? <img src={img} alt={job.design_name || ''} /> : <p className="bv-muted">{t('school.purgedFull')}</p>}</div>
    </Modal>
  );
}

function QueueTab() {
  const { t } = useTranslation('bv');
  const navigate = useNavigate();
  const { refreshPendingCount } = useOrgQueue();
  const [lists, setLists] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reject, setReject] = useState(null);
  const [note, setNote] = useState('');
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    const st = ['pending', 'approved', 'printed', 'rejected'];
    const rs = await Promise.all(st.map((s) => listQueue(s)));
    const bad = rs.find((r) => !r.ok);
    if (bad) { setErr(bad.error); setLists({ pending: [], approved: [], printed: [], rejected: [] }); return; }
    setLists(Object.fromEntries(st.map((s, i) => [s, rs[i].jobs || []])));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const all = useMemo(() => (lists ? [...lists.pending, ...lists.approved, ...lists.printed, ...lists.rejected] : []), [lists]);
  const groups = useMemo(() => groupJobs(all), [all]);
  const counts = useMemo(() => {
    const c = { all: groups.length, pending: 0, approved: 0, printed: 0, failed: 0, rejected: 0 };
    for (const g of groups) c[view(g.jobs[0])] = (c[view(g.jobs[0])] || 0) + 1;
    return c;
  }, [groups]);
  const shown = filter === 'all'
    ? [...groups].sort((a, b) => (view(a.jobs[0]) === 'pending' ? -1 : 0) - (view(b.jobs[0]) === 'pending' ? -1 : 0) || new Date(b.jobs[0].created_at) - new Date(a.jobs[0].created_at))
    : groups.filter((g) => view(g.jobs[0]) === filter);
  const printedMonth = lists ? lists.printed.filter((j) => thisMonth(j.printed_at)) : [];
  const ink = printedMonth.reduce((s, j) => s + Number(j.ink_estimate_cost_usd || 0), 0);
  const teachers = new Set(printedMonth.map((j) => j.requester_email || j.requester_name)).size;
  const after = async () => { await load(); refreshPendingCount(); };

  // Desktop "Approve & Print": approve the whole document, then every page goes to Print with its job id.
  const approve = async (g) => {
    setBusy(g.id); setErr(null);
    const r = g.jobs.length > 1 && g.groupId ? await approveGroup(g.groupId) : await approveJob(g.jobs[0].id);
    setBusy(null);
    if (!r.ok) { setErr(r.error); after(); return; }
    const pages = pagesToPrint(r.jobs || [r]);
    after();
    if (pages.length) { handToPrint(pages); navigate('/print'); }
  };
  const approveAll = async () => {
    setBusy('all');
    for (const g of groups.filter((x) => view(x.jobs[0]) === 'pending')) {
      if (g.groupId) await approveGroup(g.groupId); else await approveJob(g.jobs[0].id);
    }
    setBusy(null); after();
  };
  const print = async (g) => {
    setBusy(g.id); setErr(null);
    const pages = [];
    for (const j of g.jobs) {
      const r = await jobImage(j.id);
      if (!r.ok || !r.imageUrl) { setBusy(null); setErr(r.error || t('school.purgedFull')); return; }
      pages.push({ ...r, id: j.id, design_name: j.design_name });
    }
    setBusy(null);
    handToPrint(pagesToPrint(pages));
    navigate('/print');
  };
  const doReject = async () => {
    const g = reject; setReject(null); setBusy(g.id); setErr(null);
    const r = g.jobs.length > 1 && g.groupId ? await rejectGroup(g.groupId, note.trim()) : await rejectJob(g.jobs[0].id, note.trim());
    if (!r.ok) setErr(r.error);
    setBusy(null); setNote(''); after();
  };

  if (!lists) return <div className="bv-school__stack">{[0, 1, 2].map((i) => <Skeleton key={i} height={100} radius={24} />)}</div>;
  return (
    <div className="bv-school__stack">
      <div className="bv-school__stats" data-testid="school-stats">
        <StatCard icon="printables" color="yellow" value={counts.pending} label={t('school.stats.waiting')} />
        <StatCard icon="print" color="green" value={printedMonth.length} label={t('school.stats.printed')} />
        <StatCard icon="drop" color="blue" value={money(ink) || '$0.00'} label={t('school.stats.ink')} />
        <StatCard icon="users" color="purple" value={teachers} label={t('school.stats.teachers')} />
      </div>
      <div className="bv-school__filters">
        <div className="bv-school__chips" role="group" aria-label={t('school.statusAria')}>
          {[['all', 'common.all'], ['pending', 'status.pending'], ['approved', 'status.approved'], ['printed', 'status.printed'], ['failed', 'school.status.failed'], ['rejected', 'status.rejected']].map(([k, l]) => (
            <Chip key={k} selected={filter === k} dot={k === 'all' ? undefined : STATUS_ROW[k].dot} count={k === 'all' ? undefined : counts[k] || 0} onClick={() => setFilter(k)}>{t(l)}</Chip>
          ))}
        </div>
        {counts.pending > 0 && <Button icon="check" disabled={busy === 'all'} onClick={approveAll} data-testid="approve-all">{busy === 'all' ? t('school.approving') : t('school.approveAll', { n: counts.pending })}</Button>}
      </div>
      {err && <p className="bv-error-text" role="alert">{err}</p>}
      {shown.length === 0 ? (
        <Card><EmptyState icon="school" title={filter === 'pending' || filter === 'all' ? t('school.empty.waiting') : t('school.empty.here')}>{t('school.empty.body')}</EmptyState></Card>
      ) : (
        <div className="bv-school__rows" data-testid="queue">
          {shown.map((g) => <QueueRow key={g.id} g={g} busy={busy === g.id} onApprove={() => approve(g)} onReject={() => { setNote(''); setReject(g); }} onPrint={() => print(g)} onPreview={() => setPreview(g.jobs[0])} />)}
        </div>
      )}
      {preview && <PreviewModal job={preview} onClose={() => setPreview(null)} />}
      <Modal open={!!reject} title={reject && reject.jobs.length > 1 ? t('school.rejectAllQ', { n: reject.jobs.length }) : t('school.rejectQ')} onClose={() => setReject(null)}>
        <label className="bv-label" htmlFor="bv-rnote">{t('school.reason')}</label>
        <textarea id="bv-rnote" className="bv-textarea bv-school__note" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('school.reasonPlaceholder')} />
        <div className="bv-modal-actions">
          <Button onClick={() => setReject(null)}>{t('common.cancel')}</Button>
          <Button variant="coral" onClick={doReject} data-testid="confirm-reject">{t('school.reject')}</Button>
        </div>
      </Modal>
    </div>
  );
}

/** Org admin: handoff tabs; the queue in the handoff look, Users / Licenses / Activity from the StudioCore console. */
function AdminView() {
  const { t } = useTranslation('bv');
  const { pendingCount } = useOrgQueue();
  const [tab, setTab] = useState('queue');
  return (
    <div className="bv-school__stack" data-testid="org-console">
      <Tabs label={t('nav.school')} value={tab} onChange={setTab} tabs={[
        { value: 'queue', label: <>{t('school.tabs.queue')}{pendingCount > 0 && <span className="bv-tab__count">{pendingCount}</span>}</> },
        { value: 'users', label: t('school.tabs.users') }, { value: 'licenses', label: t('school.tabs.licenses') }, { value: 'activity', label: t('school.tabs.activity') },
      ]} />
      {tab === 'queue' && <QueueTab />}
      {tab !== 'queue' && (
        <div className="bv-school__console">
          {tab === 'users' && <UsersTab />}
          {tab === 'licenses' && <LicensesTab />}
          {tab === 'activity' && <ActivityTab />}
        </div>
      )}
    </div>
  );
}

export default function SchoolPage() {
  const { t } = useTranslation('bv');
  const [me, setMe] = useState(null);
  useEffect(() => { schoolMe().then((r) => setMe(r.ok ? r : { error: r.error })); }, []);
  return (
    <div className="bv-content bv-school">
      <TopBar title={t('nav.school')} subtitle={me && me.isOrgAdmin ? (me.schoolName ? t('school.subtitleAdminAt', { school: me.schoolName }) : t('school.subtitleAdmin')) : t('school.subtitle')} />
      {!me ? <Skeleton height={300} radius={24} /> : me.error ? <p className="bv-error-text">{me.error}</p>
        : me.isOrgAdmin ? <AdminView /> : <MyRequests me={me} />}
    </div>
  );
}
